/* Hayamoon Virtual Try-On — storefront widget (vanilla JS, no dependencies).
 * Mounts on [data-hm-tryon]. Renders a "Virtual Try-On" trigger button;
 * clicking it opens the try-on experience in a modal popup.
 * Per-product renders come inlined from the product metafield by Liquid.
 * Renders nothing when the product has no try-on images yet.
 */
(function () {
  "use strict";

  var WRISTS = ["thin", "standard", "thick"];
  var SKINS = ["light", "medium", "deep"];
  var WRIST_SUB = { thin: "< 7.5&#8243;", standard: "7.5&#8243; &ndash; 8.5&#8243;", thick: "> 8.5&#8243;" };
  var SKIN_COLORS = { light: "#f0d4ae", medium: "#c68e5f", deep: "#6f4a2f" };
  var FIT_NAMES = { "0.25": "Snug", "0.5": "Classic", "0.75": "Relaxed" };

  function cap(s) {
    return s.charAt(0).toUpperCase() + s.slice(1);
  }

  function el(html) {
    var t = document.createElement("template");
    t.innerHTML = html.trim();
    return t.content.firstChild;
  }

  function init(root) {
    // Test gate: the widget only appears on the private preview link.
    // Public visitors never see the button or the popup.
    try {
      if (new URLSearchParams(window.location.search).get("tryon_preview") !== "hayamoon-beta") return;
    } catch (e) {
      return;
    }

    var productId = root.getAttribute("data-product-id");
    var proxyBase = root.getAttribute("data-proxy-base") || "/apps/tryon";
    var showFinder = root.getAttribute("data-show-size-finder") !== "false";
    var state = { wrist: "standard", skin: "medium", fit: 0.5, images: {} };

    function hydrate(list) {
      var arr = list || [];
      if (!arr.length) return; // No try-on images yet: render nothing.
      arr.forEach(function (img) {
        state.images[img.wrist + "_" + img.skin] = img;
      });
      renderTrigger(root, state, showFinder);
    }

    // Phase 1: try-on data inlined from the product metafield by Liquid —
    // no backend / app proxy needed.
    var inline = root.getAttribute("data-tryon-images");
    if (inline) {
      try {
        var parsed = JSON.parse(inline);
        if (parsed && parsed.images && parsed.images.length) {
          hydrate(parsed.images);
          return;
        }
      } catch (e) {
        /* fall through to the proxy */
      }
    }

    fetch(proxyBase + "/product/" + encodeURIComponent(productId), {
      headers: { Accept: "application/json" },
    })
      .then(function (res) {
        if (!res.ok) throw new Error("bad response");
        return res.json();
      })
      .then(function (data) {
        hydrate(data.tryon_images);
      })
      .catch(function () {
        /* Fail silent: product page works without the widget. */
      });
  }

  // Inline trigger button on the product page. The full try-on UI lives
  // in the modal opened by openModal().
  function renderTrigger(root, state, showFinder) {
    var btn = el(
      '<button type="button" class="hm-tryon-trigger" aria-haspopup="dialog">' +
        '<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" stroke-width="1.8"/><circle cx="12" cy="12" r="3.5" fill="none" stroke="currentColor" stroke-width="1.8"/></svg>' +
        "<span>Virtual Try-On</span>" +
      "</button>"
    );
    btn.addEventListener("click", function () {
      openModal(root, state, showFinder);
    });
    root.appendChild(btn);
  }

  function openModal(root, state, showFinder) {
    if (document.querySelector(".hm-tryon-overlay")) return; // one at a time

    var overlay = el(
      '<div class="hm-tryon-overlay">' +
        '<div class="hm-tryon-dialog" role="dialog" aria-modal="true" aria-label="Virtual try-on">' +
          '<button type="button" class="hm-tryon-close" aria-label="Close virtual try-on">&times;</button>' +
          '<div class="hm-tryon-modal-body"></div>' +
        "</div>" +
      "</div>"
    );
    document.body.appendChild(overlay);
    document.body.classList.add("hm-tryon-open");

    var body = overlay.querySelector(".hm-tryon-modal-body");
    renderWidget(body, state, showFinder);

    var trigger = root.querySelector(".hm-tryon-trigger");
    var closeBtn = overlay.querySelector(".hm-tryon-close");

    function close() {
      overlay.remove();
      document.body.classList.remove("hm-tryon-open");
      document.removeEventListener("keydown", onKey);
      if (trigger) trigger.focus();
    }

    function onKey(e) {
      if (e.key === "Escape") close();
    }

    closeBtn.addEventListener("click", close);
    overlay.addEventListener("click", function (e) {
      if (e.target === overlay) close(); // backdrop click closes
    });
    document.addEventListener("keydown", onKey);
    closeBtn.focus();
  }

  // The full try-on experience, rendered inside the modal.
  function renderWidget(container, state, showFinder) {
    var wristBtns = WRISTS.map(function (w) {
      return (
        '<button type="button" data-wrist="' + w + '"' + (w === state.wrist ? ' class="active"' : "") + ">" +
        cap(w) + "<small>" + WRIST_SUB[w] + "</small></button>"
      );
    }).join("");

    var skinBtns = SKINS.map(function (s) {
      return (
        '<button type="button" data-skin="' + s + '"' + (s === state.skin ? ' class="active"' : "") + ">" +
        '<i style="background:' + SKIN_COLORS[s] + '"></i>' + cap(s) + "</button>"
      );
    }).join("");

    container.appendChild(
      el(
        '<div class="hm-tryon-inner">' +
          '<div class="hm-tryon-title">Virtual Try-On</div>' +
          '<p class="hm-tryon-sub">See this bracelet on your wrist.</p>' +
          '<div class="hm-tryon-viewer"><img alt="Virtual try-on preview"></div>' +
          '<div class="hm-tryon-label">Wrist size</div>' +
          '<div class="hm-tryon-pills" data-group="wrist">' + wristBtns + "</div>" +
          '<div class="hm-tryon-label">Skin tone</div>' +
          '<div class="hm-tryon-swatches" data-group="skin">' + skinBtns + "</div>" +
          (showFinder
            ? '<div class="hm-tryon-sizer">' +
              '<div class="hm-tryon-sizer-title">Find your size</div>' +
              '<p class="hm-tryon-sizer-sub">Measure your wrist, pick a fit, get a recommendation.</p>' +
              '<div class="hm-tryon-label">Your wrist (inches)</div>' +
              '<input type="number" step="0.1" min="5" max="12" placeholder="e.g. 7.5" inputmode="decimal" aria-label="Your wrist measurement in inches">' +
              '<div class="hm-tryon-label">Fit preference</div>' +
              '<div class="hm-tryon-pills" data-group="fit">' +
              '<button type="button" data-fit="0.25">Snug<small>+0.25&#8243;</small></button>' +
              '<button type="button" data-fit="0.5" class="active">Classic<small>+0.5&#8243;</small></button>' +
              '<button type="button" data-fit="0.75">Relaxed<small>+0.75&#8243;</small></button>' +
              "</div>" +
              '<button type="button" class="hm-tryon-cta">Recommend my size</button>' +
              '<div class="hm-tryon-result" hidden></div>' +
              "</div>"
            : "") +
          "</div>"
      )
    );

    var img = container.querySelector(".hm-tryon-viewer img");
    function update() {
      var key = state.wrist + "_" + state.skin;
      var item = state.images[key];
      if (item && item.url) {
        img.src = item.url;
        img.alt = item.label || "Virtual try-on preview";
      }
    }

    container.addEventListener("click", function (e) {
      var b = e.target.closest("button");
      if (!b) return;
      if (b.hasAttribute("data-wrist")) {
        state.wrist = b.getAttribute("data-wrist");
        syncGroup(container, "wrist", state.wrist);
        update();
      } else if (b.hasAttribute("data-skin")) {
        state.skin = b.getAttribute("data-skin");
        syncGroup(container, "skin", state.skin);
        update();
      } else if (b.hasAttribute("data-fit")) {
        state.fit = parseFloat(b.getAttribute("data-fit"));
        syncGroup(container, "fit", String(state.fit));
      } else if (b.classList.contains("hm-tryon-cta")) {
        recommend(container, state, update);
      }
    });

    update();
  }

  function syncGroup(container, group, val) {
    var attr = group === "fit" ? "data-fit" : "data-" + group;
    container.querySelectorAll('[data-group="' + group + '"] button').forEach(function (b) {
      b.classList.toggle("active", b.getAttribute(attr) === val);
    });
  }

  function recommend(container, state, update) {
    var input = container.querySelector(".hm-tryon-sizer input");
    var box = container.querySelector(".hm-tryon-result");
    var w = parseFloat(input.value);
    if (!w || w < 5 || w > 12) {
      box.hidden = false;
      box.textContent = "Please enter your wrist measurement in inches (e.g. 7.5).";
      return;
    }
    var rec = Math.ceil((w + state.fit) * 2) / 2; // round UP to nearest 0.5"
    var cat = w < 7.5 ? "thin" : w <= 8.5 ? "standard" : "thick";
    state.wrist = cat;
    syncGroup(container, "wrist", cat);
    update();
    box.hidden = false;
    box.innerHTML =
      "Recommended bracelet size: <strong>" + rec.toFixed(1) + "&#8243;</strong><br>" +
      "Based on a " + w.toFixed(1) + "&#8243; wrist with a " +
      FIT_NAMES[String(state.fit)].toLowerCase() + " fit (+" + state.fit + "&#8243;).<br>" +
      "Preview above now shows this bracelet on a " + cat + " wrist.";
  }

  document.querySelectorAll("[data-hm-tryon]").forEach(init);
})();
